import React, { useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import moment from 'moment'
import Container from '../../../../components/Container'
import api from '../../../../services/api'
import { useToast } from '../../../../hooks/toast'
import { useLoading } from '../../../../hooks/loading'
import { Alert } from '../../../../components/Alert'
import { nameActions } from '../domain/info'
import { apiDelete, apiList } from '../domain/api'
import { breadcrumbView } from '../domain/breadcrumb'
import {
  toolsViewCreate,
  toolsViewDelete,
  toolsViewUpdate,
  toolsViewList
} from '../domain/tools'
import { INPUT_MASK } from '../../../../common/constants'
import { Upload } from '../components/Upload'
import { getFileNameWithOutHash } from '../../../../utils/getFileNameWithOutHash'

const AdvertisementsView = (): JSX.Element => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const { addToast } = useToast()
  const { disableLoading, activeLoading } = useLoading()
  // eslint-disable-next-line func-call-spacing
  const [advertisement, setAdvertisement] = useState<
    (AdvertisementsApiResponse & { image_file?: File }) | null
  >(null)
  const [alertRemove, setAlertRemove] = useState(false)

  useEffect(() => {
    async function loadAdvertisement(): Promise<void> {
      activeLoading()
      try {
        const response = await api.get<AdvertisementsApiResponse>(apiList(id))
        const newAdvertisement: { image_file?: File } = {}

        if (
          response.data.image_url &&
          response.data.image &&
          response.data.image !== 'null'
        ) {
          await fetch(response.data.image_url)
            .then(res => res.blob())
            .then(blob => {
              const fileName =
                getFileNameWithOutHash(response.data.image || '') ||
                `anuncio-${response.data.id}.jpg`
              newAdvertisement.image_file = new File([blob], fileName)
            })
            .catch(error => {
              console.error('Erro ao baixar o arquivo:', error)
            })
        }

        setAdvertisement({ ...response.data, ...newAdvertisement })
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro ao carregar o anúncio',
          description:
            'Houve um erro ao carregar o anúncio, tente novamente mais tarde!'
        })
        history.push(nameActions.read.to)
      } finally {
        disableLoading()
      }
    }
    loadAdvertisement()
  }, [activeLoading, addToast, disableLoading, history, id])

  const handlerOnClickButtonConfirmRemove = async (itemId: number) => {
    try {
      await api.delete(apiDelete(String(itemId)))
      setAlertRemove(false)
      addToast({
        type: 'success',
        title: 'Anúncio removido com sucesso.'
      })
      history.push(nameActions.read.to)
    } catch (err: any) {
      setAlertRemove(false)
      if (err.response?.status === 403) {
        addToast({
          type: 'error',
          title: err.response.data.message
        })
        return
      }
      addToast({
        type: 'error',
        title: 'Anúncio não removido.'
      })
    }
  }

  return (
    <div className="d-flex flex-column container-fluid">
      <Container
        pageTitle={nameActions.read.name}
        breadcrumb={breadcrumbView}
        tools={[
          toolsViewUpdate(String(id)),
          toolsViewDelete(() => setAlertRemove(true)),
          toolsViewCreate(),
          toolsViewList()
        ]}
      >
        <div className="form-body">
          <div className="row mb-5">
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6">Cód.</label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {advertisement?.id}
                </p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6">Título</label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {advertisement?.title}
                </p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6">Link</label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {advertisement?.link || '-'}
                </p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6">Ativo</label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {advertisement ? (advertisement.active ? 'Sim' : 'Não') : '-'}
                </p>
              </div>
            </div>
            <div className="col-md-12">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6">Descrição</label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {advertisement?.description || '-'}
                </p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6">
                  Cadastrado em
                </label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {advertisement?.created_at
                    ? moment(advertisement.created_at).format(
                        INPUT_MASK.DATE_LOCALE_WITH_TIME
                      )
                    : '-'}
                </p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6">
                  Atualizado em
                </label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {advertisement?.updated_at
                    ? moment(advertisement.updated_at).format(
                        INPUT_MASK.DATE_LOCALE_WITH_TIME
                      )
                    : '-'}
                </p>
              </div>
            </div>
          </div>
          {advertisement?.image_file && (
            <div className="card mb-5">
              <div className="card-body">
                <label className="col-form-label fw-bold fs-6">Imagem</label>
                <Upload file={advertisement.image_file} />
              </div>
            </div>
          )}
        </div>
      </Container>

      <Alert
        message={`Tem certeza que deseja excluir o registro ${advertisement?.id} ?`}
        onClickCancellButton={() => setAlertRemove(false)}
        onClickConfirmButton={() =>
          handlerOnClickButtonConfirmRemove(Number(advertisement?.id))
        }
        isActive={alertRemove}
      />
    </div>
  )
}

export default AdvertisementsView
